import { Account, IssueCounts } from '@helios/generic-components'

/**
 * Sum the issue count object on the account
 */
export function sumBreakCount(issueCount?: IssueCounts): number {
  let sum = 0

  if (!issueCount) {
    return sum
  }

  sum += issueCount.found?.high?.total ?? 0
  sum += issueCount.found?.medium?.total ?? 0
  sum += issueCount.waiting?.high?.total ?? 0
  sum += issueCount.waiting?.medium?.total ?? 0

  return sum
}

/**
 * Loop through the different issue types and return the sum of all breaks
 */
export function sumBreaksForAccount(account: Account): number {
  const issueCounts = getNonNullBreakCounts(account)

  return issueCounts.reduce(
    (sum, issueCount) => sum + sumBreakCount(issueCount),
    0,
  )
}

/**
 * Get the issue counts for high priority breaks and return an issueCounts
 * object for the IssueType cell renderer
 */
export function getIssueCountsForBreaksByLevel(
  account: Account,
  level: 'high' | 'medium',
): IssueCounts {
  const issueCounts = getNonNullBreakCounts(account)
  return mergeCountsByLevel(issueCounts, level)
}

/**
 *
 */
export function mergeCounts(issueCounts: IssueCounts[]): IssueCounts {
  const highCounts = mergeCountsByLevel(issueCounts, 'high')
  const mediumCounts = mergeCountsByLevel(issueCounts, 'medium')
  const counts: IssueCounts = {}
  if (highCounts.found || mediumCounts.found)
    counts.found = {
      high: highCounts.found?.high,
      medium: mediumCounts.found?.medium,
    }
  if (highCounts.waiting || mediumCounts.waiting)
    counts.waiting = {
      high: highCounts.waiting?.high,
      medium: mediumCounts.waiting?.medium,
    }
  return counts
}

/**
 *
 */
export function mergeCountsByLevel(
  issueCounts: IssueCounts[],
  level: 'high' | 'medium',
): IssueCounts {
  let sumFound = 0
  let sumWaiting = 0

  issueCounts.forEach(issueCount => {
    sumFound += issueCount.found?.[level]?.total ?? 0
    sumWaiting += issueCount.waiting?.[level]?.total ?? 0
  })

  const results: IssueCounts = {}

  if (sumFound > 0) {
    results.found = {
      [level]: {
        total: sumFound,
      },
    }
  }

  if (sumWaiting > 0) {
    results.waiting = {
      [level]: {
        total: sumWaiting,
      },
    }
  }

  return results
}

/**
 * Get count of all held breaks regardless of level
 */
export function getIssueCountsForHeldBreaks(account: Account): number {
  let sum = 0
  const issueCounts = getNonNullBreakCounts(account)
  issueCounts.forEach(issueCount => {
    sum += issueCount.waiting?.medium?.total ?? 0
    sum += issueCount.waiting?.high?.total ?? 0
  })

  return sum
}

/**
 * Get the non null break issue categories
 */
function getNonNullBreakCounts(account: Account): IssueCounts[] {
  if (!account) {
    return []
  }

  return [
    account.otherIssues,
    account.positionIssues,
    account.corporateActionsIssues,
    account.amortizationIssues,
    account.distributionIssues,
    account.lotEntryAndExitIssues,
    account.priceAndAnalyticsIssues,
    account.securityCreationIssues,
    account.transactionBreaksIssues,
    account.transactionDataIntegrityIssues,
  ].filter(x => x != null) as IssueCounts[]
}
